import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import { Context } from "effect";
import { Schema as S } from "effect";
export const WorkEmailTokenPayload = S.Struct({ token: S.NonEmptyString });
/**
 * What following a mailed confirmation link came to. The caller holds the
 * token, which proves control of the mailbox, so telling them how it ended
 * is no enumeration oracle — and the page needs it to say what to do next.
 *
 * `app_uses_employee_password`, on `linked` and `provisioned`: the link
 * joined the Dearest account to a person who already has a password for
 * their employee account, so the app signs them in with that one from now
 * on, not the app's own.
 */
export const WorkEmailConfirmResult = S.Struct({
    outcome: S.Literal("linked", "provisioned", "already_confirmed", "held", "unmatched", "expired", "superseded", "cancelled", "invalid"),
    app_uses_employee_password: S.optional(S.Boolean),
});
/**
 * What the link's page shows before anybody decides. `open` names the
 * Dearest account that asks — its sign-in address, masked — and the work
 * address the link went to; anything else is how the request ended.
 * Read-only: previewing never changes state.
 */
export const WorkEmailPreviewResult = S.Union(S.Struct({
    outcome: S.Literal("open"),
    /** `null` for a sign-up started on the web, which no account asked for. */
    requester: S.NullOr(S.String),
    email: S.String,
}), S.Struct({
    outcome: S.Literal("expired", "superseded", "cancelled", "already_confirmed", "held", "unmatched", "invalid"),
}));
/** "This wasn't me": the request is closed and nothing is linked. */
export const WorkEmailCancelResult = S.Struct({
    outcome: S.Literal("cancelled", "already_confirmed", "held", "unmatched", "superseded", "invalid"),
});
/**
 * `sent`: a fresh link went to the same address and the old one stopped
 * working. `superseded`: a newer link was already sent, and only that one
 * renews. Anything else is how the request had already ended.
 */
export const WorkEmailRenewResult = S.Struct({
    outcome: S.Literal("sent", "already_confirmed", "held", "unmatched", "superseded", "invalid"),
});
/**
 * The requesting account, or the work address, has had its share of
 * confirmation mails for now. Answered like the other rate limits
 * (`WebSignupRateLimited`, `LoginMethodsRateLimited`): a 429 with the seconds
 * until a retry can succeed. Nothing was written or mailed.
 */
export const WorkEmailRateLimited = S.Struct({
    _tag: S.Literal("WorkEmailRateLimited"),
    retryAfterSeconds: S.Number,
});
/** The switch is off: the routes answer as though they did not exist. */
export const WorkEmailConfirmationUnavailable = S.Struct({
    code: S.Literal("NOT_FOUND"),
    message: S.String,
});
/**
 * Previewing, completing, cancelling and renewing a work-email
 * confirmation. Public: the mailed token is the proof, so the group carries
 * no principal middleware and an anonymous request reaches it through the
 * /web/v1 bridge. Completion runs here, web-side, whichever device the link
 * was opened on — and only on an explicit POST from the page's confirm
 * button: every route is a POST with the token in the body, so a link
 * fetched by a person's browser or a mail scanner never completes anything.
 */
export const workEmailConfirmationGroup = HttpApiGroup.make("workEmailConfirmation")
    .add(HttpApiEndpoint.post("preview", "/identity/work-email/preview")
    .setPayload(WorkEmailTokenPayload)
    .addSuccess(WorkEmailPreviewResult)
    .addError(WorkEmailConfirmationUnavailable, { status: 404 }))
    .add(HttpApiEndpoint.post("cancel", "/identity/work-email/cancel")
    .setPayload(WorkEmailTokenPayload)
    .addSuccess(WorkEmailCancelResult)
    .addError(WorkEmailConfirmationUnavailable, { status: 404 }))
    .add(HttpApiEndpoint.post("confirm", "/identity/work-email/confirm")
    .setPayload(WorkEmailTokenPayload)
    .addSuccess(WorkEmailConfirmResult)
    .addError(WorkEmailConfirmationUnavailable, { status: 404 }))
    .add(HttpApiEndpoint.post("renew", "/identity/work-email/renew")
    .setPayload(WorkEmailTokenPayload)
    .addSuccess(WorkEmailRenewResult)
    .addError(WorkEmailConfirmationUnavailable, { status: 404 })
    .addError(WorkEmailRateLimited, { status: 429 }));
/**
 * Signing up for Dearest on the web with a work address (PMO-371, Path B).
 * The answer is the same whether or not a mail went out, so the route says
 * nothing about which addresses a customer covers or who has an account.
 * The caller's own budget (`WebSignupGate`) and the typed address's mail
 * budget both answer 429 `WebSignupRateLimited`.
 */
export const WebSignupPayload = S.Struct({
    email: S.NonEmptyString,
    language: S.Literal("de-DE", "en-US"),
});
export const WebSignupResult = S.Struct({
    outcome: S.Literal("sent", "invalid_address"),
});
export const webSignupGroup = HttpApiGroup.make("webSignup").add(HttpApiEndpoint.post("requestSignup", "/identity/work-email/signup")
    .setPayload(WebSignupPayload)
    .addSuccess(WebSignupResult)
    .addError(WorkEmailConfirmationUnavailable, { status: 404 }));
/** The host supplies only deployment configuration and invitation URL signing. */
export class WorkEmailConfirmationHost extends Context.Tag("@pima/web-api/WorkEmailConfirmationHost")() {
}
/** The outbox message dearest-api consumes to stamp `dearest.yp_users.person_id`. */
export const DEAREST_ACCOUNT_LINKED_TYPE = "identity.dearest-account-linked";
//# sourceMappingURL=work-email-confirmation.js.map